/*
  Sacrasoul — PostHog EU behavioral analytics loader
  --------------------------------------------------
  Extracted from layout/theme.liquid (2026-08-02). It used to sit inline in the
  <head>: 21.7KB of parser-blocking, uncacheable script re-sent with every HTML
  document. As a deferred external asset it is fetched in parallel, cached
  across navigations, and runs after the document is parsed.

  WHAT THIS IS WORTH, measured, not asserted (tests/browser/head-fcp-proof.mjs,
  2026-08-02): 7119 gzip bytes off every HTML document, and 20 ms of median FCP
  on a throttled mobile link with everything else held fixed. On a live-network
  run the effect is smaller than CDN variance and cannot be resolved at all.

  It is NOT a diagnosis of the 1550 -> 1961 ms Polish homepage FCP regression
  that prompted the extraction. That regression is 411 ms and remains
  undiagnosed. The cohort move from 20% to 100% on 2026-07-25 could not have
  caused it: the cohort gate below (`bucket() >= COHORT_PCT`) runs after
  the browser has already downloaded and compiled the whole block, so the
  excluded 80% paid the same parse cost the included 20% did. And PL is EEA:
  google-consent-mode defaults analytics_storage to denied, so initPosthog()
  does not run before first paint at any cohort percentage.

  Ordering. A `defer` script runs after parsing and before DOMContentLoaded, so
  the consent watcher starts where it used to (the readyState !== "loading"
  branch at the foot of this file was already the live path whenever execution
  landed late). The one real difference: the sacrasoul:behavior listener now
  installs after full parse, so a body-inline script that dispatches during load
  can beat it. Both dispatchers (snippets/popup-email.liquid,
  assets/sacrasoul-cart-boot.js) are interaction-gated, and since 2026-09-27
  the pre-init queue keeps events whatever the consent state (before a grant
  they are sent cookieless). scripts/tests/ss-posthog-consent.harness.cjs holds both
  timings to the same outcomes.

  The one value this file cannot compute is the Shopify asset URL for
  ss-experience-rum.js. layout/theme.liquid passes it on the script tag as
  data-rum-src.

  BOOT TIMING CHANGED 2026-09-11. Consent still decides IF anything runs; it no
  longer decides WHEN. Nothing new runs before consent. See the "Boot timing"
  block near the foot of this file for the gate, and the config block for what
  was switched off and, more importantly, what was left on and why.

  WHAT THIS FILE CANNOT SET, and Sho has to click in the PostHog project
  (eu.posthog.com, project 37597, Session replay settings). Read 2026-09-11 by
  read-only API; recommended, not applied. The client is the only selector —
  it calls startSessionRecording() itself, with no sampling override — so a
  project-side sampling rate or event/URL trigger does not narrow WHO gets
  recorded, it only randomly drops some fraction of the sessions the client
  already deliberately started:
    - session_recording_sample_rate: 100% project-side. The client's trigger
      arm only starts recording once a signal already fired, so a sub-100%
      project rate would coin-flip away a fraction of
      arms the client made on purpose.
    - No project-side event/URL triggers. The client's own gate
      (isReplayTriggerEvent below) already decides
      WHEN to start; a project-side trigger on top of it is a second,
      redundant selector that can only narrow what the client already chose,
      never add a session the client did not arm.
    - session_recording_minimum_duration_milliseconds: keep 2000-3000. The
      client picks who records (signal events only), so a high
      floor would only drop the short sessions a cart error or rage click
      just selected: a visitor who errors and leaves inside ten seconds is
      exactly the replay wanted. Do not raise it to 10s.
    - session_recording_network_payload_capture_config: recordBody and
      recordHeaders are both true today, both should be off. posthog-js ORs
      the client value with this one, so the two `false` lines in the config
      below state our intent and cannot enforce it. Turning these off here
      is what actually stops the recorder patching window.fetch and
      XMLHttpRequest for a whole session.
    - recordCanvas: already off project-side (confirmed 2026-09-11 read) and
      neither captureCanvas nor collectFonts is written client-side either
      (see the session_recording config comment below) — no change needed,
      named here so the checklist is complete.

  REPLAY ARMING, rewritten 2026-09-12 (Sho: "record only when something goes
  wrong, double clicks or rage clicks"). Researched against the shipped
  posthog-js source (lazy-loaded-session-recorder.ts): a project-side
  sampling or event/URL trigger still starts rrweb unconditionally and
  buffers snapshots in memory until the trigger matches — it only cuts what
  gets SENT, never the client CPU of running the recorder. The init flag
  below that keeps recording off until the gate opens is the one thing that
  actually keeps rrweb from starting at all, so it stays; only WHEN it is
  told to start changed:
    - Everyone: `onEventCapturedForReplay` arms on $rageclick, $exception, or
      ss_cart_error via posthog.on('eventCaptured', ...). No pre-trigger
      buffer — the recording starts fresh at the signal, which is the
      correct trade for removing the CPU cost from every other session.
    - $dead_click joined the triggers on 2026-09-27 (Sho: "only when there is
      some kind of error or dead click"), and the 10% baseline that recorded
      ordinary sessions from the first tap was removed the same day: it was
      56% of all replays (100 of 179 in the week to 27 Sep) and none of them
      had anything wrong. Every recording now starts at a signal.

  To remove: delete this file and its tag in layout/theme.liquid. No other file
  depends on it.
*/
(function () {
  "use strict";
  var PH_TOKEN = "phc_OY86O2la99mK6CHQcFI9bwvc9rcnKJTBkFR3QMqyyYU";
  var PH_HOST  = "https://eu.i.posthog.com";
  var PH_UI    = "https://eu.posthog.com";
  var COHORT_PCT = 100;         // % of visitors included during the pilot
  var COHORT_KEY = "ss_ph_bucket";

  // ---- 1. Cohort gate: stable per-browser bucket, persisted, never flaps ----
  function bucket() {
    try {
      var v = localStorage.getItem(COHORT_KEY);
      if (v === null) { v = String(Math.floor(Math.random() * 100)); localStorage.setItem(COHORT_KEY, v); }
      return parseInt(v, 10);
    } catch (e) { return 100; } // storage blocked => exclude
  }
  /* Internal traffic. Our own browsing lands in the same numbers we use to
     decide things, and a country filter would throw away real customers in
     Oman. So a device opts itself in once, by loading any page with
     ?ss_internal=1, and stays flagged until ?ss_internal=0 clears it. A theme
     preview or the theme editor flags itself, since neither is ever a shopper.
     Events still send: they are tagged, not silenced, so nothing is lost and
     PostHog's own internal-traffic filter can do the excluding.

     REMEMBERING the opt-in and ACTING on it are two different jobs, and they
     cannot share a gate. The write used to live inside internalTraffic(), and
     internalTraffic() is reached only from initPosthog() — consent granted AND
     in cohort. Three consequences, and only the narrow ones are real:

       - EEA/UK/CH (so PL, most of the revenue): google-consent-mode defaults
         analytics_storage to DENIED there, so a device loading ?ss_internal=1
         under a pending banner persisted nothing at all.
       - ?ss_internal=0 could not clear a flag before consent either, so the
         documented escape hatch did not work when it was most likely to be used.
       - An out-of-cohort device (COHORT_PCT below 100) never remembered
         anything, at any consent state.

     What this is NOT: an explanation of the OM desktop pollution in the
     web-vitals p75. Oman is not in the region list at
     snippets/google-consent-mode.liquid:59, so analytics_storage resolves
     GRANTED on the first load there and this path already ran. The 2026-08-03
     ss_internal/country split (sacrasoul-brain/website/
     2026-08-03-fcp-finding-ss-internal-split.md) shows those samples untagged
     because those profiles were never opted in at all — not because the write
     failed. No code change retags events already in PostHog.

     Two rules the parameter now obeys, because a query string anyone can copy
     must not be able to re-classify a shopper's device:
       1. It is honoured only on a same-origin or referrer-less navigation. We
          type it or click it from our own pages. A click arriving from a
          campaign, a support reply, a search result or a crawler is not us.
       2. It is stripped from the address bar on arrival, so it cannot be
          copied out, sent onward as a referrer, or indexed.

     Consent no longer gates the remembering (decision 2026-10-05,
     sacrasoul-brain/strategy/decisions/2026-10-05-tracking-data-flows-
     regardless-of-consent-interim.md). Until then the opt-in was held in
     sessionStorage, dying with the tab, and the durable localStorage copy
     waited for an analytics grant — so on sacrasoul.pl, where the banner
     defaults to denied, an unanswered banner meant the device forgot it was
     ours with every new tab. Now ?ss_internal=1 writes all three copies at
     once: sessionStorage, localStorage, and the ss_internal=1 COOKIE.

     The cookie is the copy the server reads. The server container (Meta CAPI,
     Klaviyo, GA4 via pulse.*) cannot see storage; its "Internal Marker
     Resolver" reads only a cookie named ss_internal, which until 2026-10-05
     only the probe harness ever set — so a device marked here still reached
     Meta. It is written on the shop's registrable domain (.sacrasoul.com /
     .pl / .nl, from location.hostname) so pulse.<domain> receives it, and
     re-written on every page that finds the flag in storage or in the cookie
     itself, because Safari caps a cookie written by script at 7 days.
     A cookie found here counts as the flag. ?ss_internal=0 clears every copy,
     under the same same-origin rule as the opt-in. assets/ss-attribution.js
     re-writes the cookie the same way. */
  var INTERNAL_KEY = "ss_internal";
  var INTERNAL_COOKIE_SECONDS = 34560000; // 400 days, the longest a browser grants
  var internalOptIn = null;     // this document's own answer, once the URL is read

  function internalCookieDomain() {
    try {
      var own = String(window.location.hostname || "").match(/(?:^|\.)(sacrasoul\.(?:com|pl|nl))$/i);
      return own ? ";Domain=." + own[1].toLowerCase() : "";
    } catch (e) { return ""; }
  }

  function writeInternalCookie(seconds) {
    try {
      var secure = window.location.protocol === "https:" ? ";Secure" : "";
      var tail = ";Max-Age=" + seconds + ";Path=/;SameSite=Lax" + secure;
      document.cookie = INTERNAL_KEY + "=" + (seconds ? "1" : "") + tail + internalCookieDomain();
      // A host-only copy (an older probe, a preview domain) must go too.
      if (!seconds) document.cookie = INTERNAL_KEY + "=" + tail;
    } catch (e) {}
  }

  function internalCookieSet() {
    try {
      return /(?:^|;)\s*ss_internal=1(?:;|$)/.test(document.cookie || "");
    } catch (e) { return false; }
  }

  /* The flag as this device remembers it, in any of its three copies. */
  /* (Not written as a ladder of `return true` ending in `return false;`: that
     closing sequence is the end-mark tests/browser/internal-flag-proof.mjs
     lifts internalTraffic() by, and the first one wins.) */
  function internalRemembered() {
    var seen = internalCookieSet();
    try { if (sessionStorage.getItem(INTERNAL_KEY) === "1") seen = true; } catch (e) {}
    try { if (localStorage.getItem(INTERNAL_KEY) === "1") seen = true; } catch (e) {}
    return seen;
  }

  function readInternalParam() {
    try {
      var url = new URL(window.location.href);
      var q = url.searchParams.get(INTERNAL_KEY);
      if (q !== "1" && q !== "0") return null;
      var ref = document.referrer;
      if (ref && ref.indexOf(window.location.origin + "/") !== 0) return null;
      url.searchParams.delete(INTERNAL_KEY);
      try {
        history.replaceState(history.state, "", url.pathname + url.search + url.hash);
      } catch (e) {}
      return q;
    } catch (e) { return null; }
  }

  function persistInternalOptIn() {
    try {
      if (sessionStorage.getItem(INTERNAL_KEY) === "1") localStorage.setItem(INTERNAL_KEY, "1");
    } catch (e) {}
  }

  function rememberInternalOptIn() {
    var q = readInternalParam();
    if (q === null) return;
    internalOptIn = q === "1";
    try {
      if (q === "1") sessionStorage.setItem(INTERNAL_KEY, "1");
      else sessionStorage.removeItem(INTERNAL_KEY);
    } catch (e) {}
    if (q === "0") { try { localStorage.removeItem(INTERNAL_KEY); } catch (e) {} }
    else persistInternalOptIn();  // durable at once, at any consent state
    if (q === "0") writeInternalCookie(0);
  }
  rememberInternalOptIn();
  /* Every page of a remembered device re-writes the cookie, before anything
     else can send. This is what keeps it alive past Safari's 7 days. */
  if (internalOptIn !== false && internalRemembered()) writeInternalCookie(INTERNAL_COOKIE_SECONDS);

  /* At 100% there is nothing to draw, so nothing is written to storage before
     consent (the bucket used to be drawn for the replay baseline too). */
  if (COHORT_PCT < 100 && bucket() >= COHORT_PCT) return;

  /* Consent-granted behavior events can happen before the heavy SDK reaches an
     idle window. Hold a tiny in-memory queue so deferring analytics never loses
     a confirmed add or checkout. Nothing DURABLE is stored before consent: the
     cohort bucket and the internal opt-in above are the two exceptions, and
     since 2026-10-05 the opt-in is durable at once (see INTERNAL_KEY). */
  /* Why a REASON and not a boolean: the two reasons deserve different
     persistence. "asked" is somebody deliberately marking a profile internal
     (the durable opt-in, design mode, the preview bar) — that intent is
     per-profile and sticky, so it propagates via register() and
     setPersonProperties(). "driven" is the browser reporting it is currently
     under automation — a per-SESSION fact about control, not a per-profile
     fact about ownership, so it propagates via register_for_session() only:
     the moment the automation ends, nothing durable or person-level lingers,
     and a real shopper whose browser was once remotely driven is not
     misclassified forever after. */
  /* Our own desktop shell, recognised from what it is rather than from what it
     was once told. Paseo is the fleet's agent supervisor, and its desktop app
     is an Electron client whose windows browse the live store while the house
     works. (operations/paseo-integration.md describes the daemon and its thin
     clients; the Electron runtime and the user agent below are not in that doc
     — they come from the PostHog query cited next, which is the evidence.)

     The opt-in above cannot reach it, and no operator action would fix that.
     The opt-in is a localStorage key, so it is per-origin AND per-profile;
     an app window that is repartitioned or reinstalled throws that key away and
     silently becomes a customer again. The user agent needs no memory, so it
     holds on the first load of every window, on both origins, forever.

     Field evidence, PostHog EU project 37597, queried 2026-09-08. Every one of
     the 827 events in the last 30 days from that shell carries the literal
     token `Paseo/0.7.x`, in five build variants, all of them
     `… Paseo/0.7.x Chrome/146 Electron/41.2.0 …`, and all of them untagged.
     Split of $web_vitals FCP on sacrasoul.com/, prior 7 days vs current 7:

       cohort        prior n / p75      current n / p75
       Paseo shell        3 / 1216 ms        15 / 2518 ms
       customers         14 / 1634 ms        11 / 1802 ms

     The shell went from 3 samples to 15 AND from 1216 ms to 2518 ms, so it is
     the whole of the signal: the page p75 the scout reads moved 1634 -> 2266 ms
     and scored a 38.7% customer regression. Customers moved 1634 -> 1802 ms,
     +10.3%, which does not clear the scout's own 25% trigger. The prior
     baseline was NOT shell-inflated — the shell was faster then and only 3
     samples — so this is contamination of the current window, not of both.

     What this does NOT settle: 1802 ms sits a hair over the 1800 ms "good" FCP
     bar (tracking-scout breaches_vitals), on n=11. That is a thin sample at a
     threshold, not a demonstrated regression, and this change does not make the
     homepage faster. It only stops the shell being read as a shopper.

     Keyed on `Paseo/`, not on `Electron/`. Electron is a general-purpose
     runtime that many third-party desktop apps and browser wrappers embed, and
     tagging it wholesale would classify those visitors as staff. `Paseo/` names
     our own client and covers the identical 827 events with nothing else in
     reach. This matters because the tag is sticky: setPersonProperties is
     person-scoped and register() persists, so a wrong match costs that
     visitor's entire journey, not one sample. */
  function internalShell() {
    try {
      return (navigator.userAgent || "").indexOf("Paseo/") !== -1;
    } catch (e) { return false; }
  }

  function internalReason() {
    /* The shell outranks storage: a Paseo window with storage blocked or wiped
       is still ours, so the user agent is consulted before anything that can
       throw. It answers "asked" — being our own device is a per-profile fact
       of ownership, not a per-session fact of control, so it takes the same
       sticky doors (register + setPersonProperties) the deliberate opt-in
       does. It also DELIBERATELY outranks "driven" below: an agent-driven
       Paseo window would otherwise be tagged session-only, and the moment the
       automation ended the window would read as a shopper again. Ownership
       beats control — the machine is ours whether or not something is
       steering it. */
    if (internalShell()) return "asked";
    rememberInternalOptIn();
    if (internalOptIn === true) return "asked";
    if (internalOptIn !== false && internalRemembered()) return "asked";
    try {
      if (window.Shopify && window.Shopify.designMode) return "asked";
      if (document.getElementById("preview-bar-iframe")) return "asked";
      /* A browser under automation is not a shopper, and says so. This is a
         PARTIAL defence deliberately kept because it is free, and its limits
         matter more than its coverage:

         WHAT IT CATCHES. navigator.webdriver is the W3C automation flag. It is
         true in a browser LAUNCHED under WebDriver or by a CDP driver that does
         not suppress it — Playwright, Puppeteer and Selenium as they launch by
         default, which is what tests/browser/live-audit.mjs and every probe on
         the box are. It is false in a shipped browser a customer uses. A
         script CAN shadow the property (our own proofs do, via
         defineProperty), and remote control of a real shopper's browser is
         rare but possible — which is why this reason is session-scoped below,
         and why the worst a spoof can do is exclude one session's analytics.
         Events are tagged, never silenced; the shop itself is untouched.

         WHAT IT DOES NOT CATCH, MEASURED. A CDP client attached to an ordinary
         Chrome, a stealth-patched cloud browser, or a DevTools device-toolbar
         session all report false. Browser Use Cloud loaded sacrasoul.com on
         2026-08-15 with navigator.webdriver=false
         (sacrasoul-brain/sessions/aside/2026-08-15-browser-use-replacement-live-test.md).
         So this does NOT cover the whole fleet, and it is not the primary
         defence. The primary defence is still the durable localStorage opt-in
         above, set once per profile per origin
         (sacrasoul-brain/skills/live-store-testing/SKILL.md). Any fleet surface
         that keeps a profile across sessions — Browser Use named profiles and
         Aside's Chrome both do — CAN hold that flag and must be given it. Do
         not read this check as a reason to stop setting it.

         WHAT IT IS NOT EVIDENCE OF. The 2026-09-06 finding ("English homepage
         FCP p75 1645 -> 2239 ms") is NOT closed by this line, and this line is
         not proven to have caused it. The suspect samples are dominated by one
         $device_id (measured 2026-09-08: 15 of the window's 27 samples, 13
         sessions over 30 days, ten distinct viewports on one desktop Chrome);
         a profile that survives that many sessions is a
         persistent-profile browser, which is exactly the shape
         tests/browser/_internal.mjs already reasoned CANNOT be a thrown-away
         Playwright context — and persistent-profile surfaces are the ones that
         report false above. navigator.webdriver was never measured on those
         profiles. See the note at the foot of
         tests/browser/internal-flag-proof.mjs for what was measured instead.

         WHAT ss_internal MEANS NOW. Wider than "the fleet's own browsing",
         which is how tracking-scout, vigil-posthog-hourly and
         services/vigil/playbook-posthog.md still describe it: any
         automation-launched client that reports the flag — scrapers driven by
         WebDriver/CDP launchers, and likely Lighthouse/PSI (expected from
         their --enable-automation launch, NOT measured here).
         Keeping synthetic load out of a customer p75 is the point, but the
         name under-describes it. Unlike "asked", this reason is deliberately
         NON-sticky: register_for_session() below tags this session's events
         only, no person property, nothing durable — see the note above
         internalReason(). Nothing already in PostHog is retagged. */
      if (navigator.webdriver === true) return "driven";
    } catch (e) {}
    return null;
  }

  function internalTraffic() {
    if (internalReason() !== null) return true;
    /* This clumsier-than-needed tail is deliberate: the exact sequence
       "return false;\n  }" is the end-mark internal-flag-proof.mjs lifts this
       region by. Collapse it into one return and the proof dies loudly. */
    return false;
  }

  /* A page served from this machine is a test page, never a shopper. Measured
     2026-10-05: 159 $pageview events in 7 days reached the production project
     from 127.0.0.1 test servers (tagged internal, but still counted). Nothing
     boots and nothing sends there. A browser proof that needs the loader to run
     on its own local server sets window.__ssPhAllowLocal = true first;
     production never defines it. Kept below internalTraffic(), outside the
     region tests/browser/internal-flag-proof.mjs lifts. */
  function localDevHost() {
    try {
      var host = String(window.location.hostname || "").toLowerCase();
      return host === "localhost" || host === "127.0.0.1" || host === "::1" || host === "[::1]" ||
        /\.local$/.test(host);
    } catch (e) { return false; }
  }
  if (localDevHost() && window.__ssPhAllowLocal !== true) return;

  var BEHAVIOR_EVENT = "sacrasoul:behavior";
  var pendingBehavior = [];
  /* Passive events (detail.passive: the Jade story's chapter views, scroll
     depth, steam steps, sent by SSD.track in assets/jade-v2-core.js) are raised
     by READING, so they ride a queue of their own: they never wake init (that
     would boot the SDK mid-scroll) and they drop their OLDEST past PASSIVE_CAP,
     so a long read can never crowd a confirmed add or checkout out of the 20
     slots above. Both queues drain together, in arrival order, at init. */
  var pendingPassive = [];
  var PASSIVE_CAP = 40;
  var pendingSeq = 0;
  var consentState = null;
  var lastConsentLabel = null;  // the ss_consent value last registered
  /* Capture and replay are two different costs. Capture is cheap and starts the
     moment consent lands, so no confirmed add or checkout is ever lost. Replay
     is the expensive rrweb snapshot, so it alone waits for a quiet moment. */
  var sdkReady = false;
  var registerContextNow = null; // set by initPosthog; re-run after a late opt-in
  var replayWanted = false;
  var beginReplay = null;
  function queueBehaviorBeforeInit(event) {
    if (window.__ssPhInit) return;
    var detail = event && event.detail;
    if (!detail || !detail.name) return;
    var item = { detail: detail, timestamp: new Date(), seq: ++pendingSeq };
    if (detail.passive) {
      pendingPassive.push(item);
      if (pendingPassive.length > PASSIVE_CAP) pendingPassive.shift();
      return;
    }
    if (pendingBehavior.length < 20) {
      pendingBehavior.push(item);
    }
    /* A confirmed add or checkout is the one thing this queue must never lose,
       and the queue only drains at init. Every real one of these follows a tap
       that has already armed the gate, so this is the belt for the emitter that
       fires without one — and it costs a hoisted call, not a timer. */
    wakeInit();
  }
  document.addEventListener(BEHAVIOR_EVENT, queueBehaviorBeforeInit);

  // ---- 2. PostHog stub (async loader) ----
  !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagResult isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);

  /* Did an exception come from a script FILE, or from no file at all?
     posthog-js fills $exception_list[].stacktrace.frames from the error's own
     stack. Three shapes mean "no file": a frame naming the page URL (an inline
     or engine-injected script — this is how WebKit attributes what an in-app
     browser pushes into its WKWebView, and what the 2026-07-27 stack showed),
     a frame with no name or "<anonymous>" (how Blink attributes the same kind
     of script), and no frames at all. Anything else is a fetched script, and a
     fetched script is somebody's code we can name. */
  function bareUrl(url) { return String(url || "").split("#")[0].split("?")[0]; }
  /* The markers Meta's iOS surfaces put in the UA: Instagram, the Facebook
     app (FBAN/FBAV/FB_IAB), and the in-app-browser module version (IABMV).
     A plain Safari UA carries none of them. */
  function inAppBrowserUA() {
    return /\b(Instagram|FBAN|FBAV|FB_IAB|IABMV)\b/.test(navigator.userAgent || "");
  }
  function thrownFromAScriptFile(exception) {
    var frames = (exception && exception.stacktrace && exception.stacktrace.frames) || [];
    var doc = bareUrl(window.location.href);
    for (var i = 0; i < frames.length; i++) {
      var file = bareUrl(frames[i] && (frames[i].filename || frames[i].abs_path));
      if (!file || file === doc || file === "<anonymous>") continue;
      return true;
    }
    return false;
  }

  function initPosthog() {
    if (window.__ssPhInit) return;
    window.__ssPhInit = true;
    var path = window.location.pathname;
    var replayProbeSession = null;
    var replayProbeGeneration = 0;
    var REPLAY_PROBE_KEY = "ss_ph_replay_diag_session_v2";

    function ssCapture(name, props, options) {
      try { posthog.capture(name, props || {}, options); } catch (e) {}
    }

    function installBehaviorBridge() {
      if (window.__ssPhBehaviorBridge) {
        pendingBehavior.length = 0;
        pendingPassive.length = 0;
        document.removeEventListener(BEHAVIOR_EVENT, queueBehaviorBeforeInit);
        return;
      }
      window.__ssPhBehaviorBridge = true;
      var eventMap = {
        cart_item_added: "ss_add_to_cart",
        cart_add_failed: "ss_add_to_cart_failed",
        cart_add_uncertain: "ss_add_to_cart_uncertain",
        // an add the shop refused on stock, not a fault: sold out, or every
        // remaining unit already in the cart. Carries reason=sold_out|cap.
        cart_add_unavailable: "ss_add_to_cart_unavailable",
        cart_opened: "ss_cart_opened",
        checkout_started: "ss_begin_checkout",
        cart_review_requested: "ss_cart_review_requested",
        cart_gift_added: "ss_cart_gift_added",
        // the free-delivery line on the first curated pairing that closes the gap (2026-09-25):
        // shown once per open per product and gap; added only on a confirmed
        // add from that row. Both carry gap, gap_cents, currency, product_handle, row_index.
        cart_nudge_shown: "ss_cart_nudge_shown",
        cart_nudge_added: "ss_cart_nudge_added",
        cart_item_restored: "ss_cart_item_restored",
        cart_error: "ss_cart_error",
        popup_shown: "ss_popup_shown",
        popup_suppressed: "ss_popup_suppressed",
        popup_closed: "ss_popup_closed",
        popup_submitted: "ss_popup_submitted",
        popup_eligible: "ss_popup_eligible",
        popup_error: "ss_popup_error",
        // Klaviyo never answered and the Shopify newsletter route stored the
        // signup instead; carries status, error, message, ms, online.
        popup_fallback: "ss_popup_fallback",
        // the email ask inside the cart drawer (assets/sacrasoul-cart-email.js)
        cart_email_shown: "ss_cart_email_shown",
        cart_email_expanded: "ss_cart_email_expanded",
        cart_email_submitted: "ss_cart_email_submitted",
        cart_email_discount_applied: "ss_cart_email_discount_applied",
        cart_email_error: "ss_cart_email_error",
        cart_email_fallback: "ss_cart_email_fallback",
        review_helpful_changed: "ss_review_helpful_changed",
        cart_add_heal_attempted: "ss_cart_add_heal_attempted",
        cart_add_heal_succeeded: "ss_cart_add_heal_succeeded",
        // the hero photo opened the full-screen viewer (PR #1752 made the photo the door)
        gallery_opened: "ss_gallery_opened",
        // the Jade v2 story's reading measurements (SSD.track, assets/jade-v2-*.js),
        // sent passive: queued before init without waking it. Names kept as they were
        // when jade-v2-core.js called posthog.capture itself.
        pdp_story_chapter_view: "pdp_story_chapter_view",
        pdp_story_scroll_depth: "pdp_story_scroll_depth",
        pdp_story_object_open: "pdp_story_object_open",
        pdp_story_steam_step: "pdp_story_steam_step",
        pdp_story_tier_change: "pdp_story_tier_change",
        pdp_story_atc_click: "pdp_story_atc_click",
        pdp_story_bar_picker_open: "pdp_story_bar_picker_open",
        pdp_story_bar_picker_select: "pdp_story_bar_picker_select",
        pdp_story_review_read: "pdp_story_review_read",
        pdp_story_review_more: "pdp_story_review_more"
      };
      function captureBehavior(detail, options) {
        var eventName = detail && eventMap[detail.name];
        if (!eventName) return;
        ssCapture(eventName, detail.properties || {}, options);
      }
      /* A behaviour event raised while the document is leaving -- the cart
         drawer files navigation-cancelled requests from its pagehide handler --
         cannot wait for the batch; it goes as a beacon or it goes nowhere. The
         emitter stamps detail.leaving itself: a flag kept here would depend on
         whose pagehide listener runs first. */
      document.addEventListener(BEHAVIOR_EVENT, function (event) {
        var detail = event && event.detail;
        captureBehavior(detail, detail && detail.leaving ? { transport: "sendBeacon" } : undefined);
      });
      pendingBehavior.concat(pendingPassive).sort(function (a, b) { return a.seq - b.seq; }).forEach(function (queued) {
        captureBehavior(queued.detail, { timestamp: queued.timestamp });
      });
      pendingBehavior.length = 0;
      pendingPassive.length = 0;
      document.removeEventListener(BEHAVIOR_EVENT, queueBehaviorBeforeInit);
    }

    function diagnoseReplayOnce(ph, sessionId) {
      if (!sessionId || replayProbeSession === sessionId) return;
      try {
        if (sessionStorage.getItem(REPLAY_PROBE_KEY) === sessionId) return;
      } catch (e) {}
      replayProbeSession = sessionId;
      var generation = ++replayProbeGeneration;
      var checks = 0;
      var retried = false;

      function check() {
        if (generation !== replayProbeGeneration || replayProbeSession !== sessionId) return;
        try {
          if (ph.get_session_id && ph.get_session_id() !== sessionId) return;
          var started = !!(ph.sessionRecordingStarted && ph.sessionRecordingStarted());
          if (!started && checks++ < 20) {
            /* Recording is now started by the attention gate rather than at
               init, so a single missed call would cost the whole session. Half
               way through the probe, ask once more. Starting twice is a no-op;
               never starting is the expensive mistake. */
            if (checks === 10 && !retried) {
              retried = true;
              try { ph.startSessionRecording(); } catch (e) {}
            }
            setTimeout(check, 500);
            return;
          }
          try { sessionStorage.setItem(REPLAY_PROBE_KEY, sessionId); } catch (e) {}
          var observation = started ? "recording_started" : "not_started_within_10s";
          ph.capture("ss_replay_status", {
            status: started ? "started" : "not_started",
            reason: observation,
            started: started,
            sdk_ready: true,
            controls_overridden: false,
            observation: observation
          });
        } catch (e) {}
      }
      check();
    }

    beginReplay = function () {
      try { startReplayDiagnostics(posthog); } catch (e) {}
    };

    function startReplayDiagnostics(ph) {
      try { ph.startSessionRecording(); } catch (e) {}
      try {
        if (ph.onSessionId) {
          ph.onSessionId(function (sessionId) { diagnoseReplayOnce(ph, sessionId); });
        }
        diagnoseReplayOnce(ph, ph.get_session_id && ph.get_session_id());
      } catch (e) {}
    }

    /* The page context every event carries: the internal flag, market and
       language, first-party attribution. Run in loaded() (before the opening
       $pageview) and again after a later opt-in, because opt_in_capturing()
       reloads persistence and drops what was registered while cookieless. */
    function registerContext(ph) {
        var internalWhy = internalReason();
        if (internalWhy === "asked") {
          try { ph.register({ ss_internal: true }); } catch (e) {}
          if (consentState === true) {
            try { ph.setPersonProperties({ ss_internal: true }); } catch (e) {}
          }
        } else if (internalWhy === "driven") {
          try { ph.register_for_session({ ss_internal: true }); } catch (e) {}
        }
        /* Market and language from the page itself, on every event. Cookieless
           events (visitors who have not granted analytics consent) lose GeoIP
           because PostHog hashes the IP away, so this is how they can still be
           split by storefront; consented events carry both. */
        /* ss_consent: what this visitor has told us, on every event, so a
           refusal and an unanswered banner can be told apart (see
           consentLabel()). It carries no identifier. */
        lastConsentLabel = consentLabel();
        try {
          ph.register({
            ss_market: (window.Shopify && window.Shopify.country) || null,
            ss_locale: document.documentElement.lang || null,
            ss_consent: lastConsentLabel
          });
        } catch (e) {}
        /* The attribution asset runs first and contains only controlled values.
           Register them before PostHog emits its initial pageview. */
        try {
          if (window.SSAttribution && window.SSAttribution.properties) {
            var attr = window.SSAttribution.properties();
            if (attr && attr.ss_channel) {
              ph.register(attr);
              var setOnce = {};
              if (attr.ss_channel !== "direct") {
                setOnce = { ss_channel: attr.ss_channel, ss_kind: attr.ss_kind,
                            ss_source: attr.ss_source, ss_campaign: attr.ss_campaign,
                            ss_first_seen: attr.ss_first_seen, ss_landing: attr.ss_landing };
              }
              if (consentState === true) {
                ph.setPersonProperties(
                  { ss_last_channel: attr.ss_last_channel, ss_last_kind: attr.ss_last_kind },
                  setOnce
                );
              }
              if (window.SSAttribution.sync) window.SSAttribution.sync();
            } else if (window.SSAttribution && window.SSAttribution.off) {
              ["ss_channel", "ss_kind", "ss_source", "ss_campaign", "ss_landing",
               "ss_first_seen", "ss_last_channel", "ss_last_kind", "ss_last_source",
               "ss_adset", "ss_ad", "ss_last_ad", "ss_visitor"].forEach(function (key) {
                try { ph.unregister(key); } catch (e) {}
              });
            }
          }
        } catch (e) {}
    }
    registerContextNow = registerContext;

    var autoPageview = consentState === true; // see capture_pageview below
    posthog.init(PH_TOKEN, {
      api_host: PH_HOST,
      ui_host: PH_UI,
      defaults: "2026-05-30",
      /* `always` creates the profile on the first consented event so initial
         acquisition properties exist without a separate identify() call. */
      person_profiles: "always",
      persistence: "localStorage+cookie",
      /* Anonymous counting without consent (Sho, 2026-09-27: "Even anonymous
         analytics are good enough, something we don't need to ask for
         marketing consent"). Until analytics consent is granted the SDK runs in
         PostHog's cookieless mode: events are sent with no cookie, no
         localStorage and no persistent id (PostHog hashes IP + user agent with
         a daily salt server-side; project setting "cookieless server hash
         mode"), no person profile, no replay. `opt_out_capturing_by_default`
         is what makes an unanswered banner count as cookieless rather than
         paused (posthog-js: isRejected() = explicit opt-out OR undecided with
         this flag). A later grant calls opt_in_capturing() in
         applyConsentState, which switches to normal tracking with a real id,
         and then re-registers the page context (registerContext).
         SINCE 2026-10-06 the cookieless branch is dormant in production:
         snippets/google-consent-mode.liquid declares analytics_storage granted
         in every region and pushes no denied update, so consentState is true
         for every visitor, declined and unanswered included: a persistent id
         (ph_* cookie + localStorage), a person profile, autocapture, flags.
         Session replay is the exception and still follows the visitor's own
         answer (replayAllowed()). The branch stays for a page whose dataLayer
         says denied, so reverting the snippet restores it unchanged. */
      /* Only for a visitor not yet consented when the SDK starts. A consented
         visitor gets exactly the pre-2026-09-27 config: under "on_reject"
         posthog-js holds the opening $pageview until opt_in_capturing() and
         sends it from inside that call, before loaded() could register
         ss_internal and attribution (posthog-sdk-order-proof A1). */
      cookieless_mode: consentState === true ? undefined : "on_reject",
      opt_out_capturing_by_default: consentState !== true,
      autocapture: true,
      /* A cookieless start sends its own opening $pageview from loaded(), after
         the consent decision and registerContext: under "on_reject" posthog-js
         holds the automatic one and releases it from inside opt_in/opt_out,
         before our registration (a grant that lands while array.js downloads). */
      capture_pageview: autoPageview,
      capture_pageleave: true,
      rageclick: true,
      /* Replay records the DOM and real interactions, nothing else.
         maskAllInputs: every typed character stays masked.
         sampling.mousemove 250: rrweb throttles mousemove/touchmove/drag to one
           sample per 250 ms instead of its 50 ms default (verified in the
           shipped recorder.js: `"number"==typeof e.mousemove?e.mousemove:50`).
           A fifth of the pointer events, the same replay to watch.
         recordHeaders/recordBody false: with either one true the recorder
           wraps the page's own fetch and XHR entry points for the whole
           session (verified in the shipped recorder.js). The project has BOTH true
           (session_recording_network_payload_capture_config, read 2026-09-11),
           and posthog-js resolves this pair as client OR server, so these two
           lines state our intent but CANNOT switch it off on their own — the
           project setting has to change too. Named in the PR.
         captureCanvas / collectFonts are deliberately absent: neither name
           appears anywhere in the shipped array.js or recorder.js. Canvas
           recording is project-side (`recordCanvas`) and off; rrweb's
           collectFonts already defaults to false. Writing them here would be
           config that reads well and does nothing. */
      session_recording: {
        maskAllInputs: true,
        recordHeaders: false,
        recordBody: false,
        sampling: { mousemove: 250 }
      },
      disable_session_recording: true,
      /* Web vitals STAY ON, against the first draft of this change. The daily
         speed tripwire routine (its MEMORY.md under sacrasoul-brain/agents/
         aside/memory-mirror/routines/posthog-pilot-speed-tripwire-daily-read-)
         reads p75 LCP, INP, CLS and FCP straight off $web_vitals, and has
         autocapture_web_vitals_opt_in true. ss-experience-rum.js does NOT
         produce them — it emits view_transition, prefetch_hit, bfcache_restore,
         cart_honesty and the INP tail only. Turning them off would blind a
         routine Sho runs, to save an 8 KiB chunk that now loads after the boot
         gate below anyway. The extension's observers all use `buffered: true`
         (verified in the shipped web-vitals.js), so a late init still reports
         the real LCP and FCP of the load.
         `network_timing` is not written here because it is not a key this SDK
         reads: zero occurrences in array.js. Replay network capture is the
         project-side setting described above. */
      capture_performance: { web_vitals: true },
      /* Dead clicks ON again (Sho, 2026-09-27): they are a replay trigger, so a
         tap that did nothing gets recorded. They were off from 2026-09-11 as a
         metric (sacrasoul-brain/website/2026-08-02-jade-dead-clicks-handoff.md
         found a mobile-only over-count in posthog-js), so read a dead-click
         replay as a lead to check, not a proven defect.
         Surveys OFF: the project has surveys_opt_in unset and this repo has no
         getSurveys/renderSurvey/canRenderSurvey caller (grep, 2026-09-11), yet
         surveys.js (34 KiB) still turned up in the PageSpeed trace.
         Recording console log OFF: it was never read, and it keeps every
         console argument out of the replay payload. */
      capture_dead_clicks: true,
      disable_surveys: true,
      enable_recording_console_log: false,
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false
      },
      /* Exceptions the storefront did not throw and cannot fix drown the ones
         it did: the Facebook/Instagram in-app WebView's own bridge errors and
         Shopify's web-pixel sandbox failing to import under a blocker. 7 days
         to 2026-09-08: 131 $exception events, 5 of them ours. Dropped at the
         source so the panel shows the store. One list, one test
         (tests/browser/posthog-exception-noise-proof.mjs): a new unconditional
         noise pattern is one line. A stackless "Script error." is deliberately
         NOT here: it is also what a first-party asset served without CORS looks
         like, which is exactly what exception tracking exists to catch.

         The iOS half of that same in-app-WebView sentence sits BELOW the list,
         because a message alone must not decide it. `window.webkit.messageHandlers`
         is the WKWebView bridge. The theme has no such call — and that is now
         enforced rather than asserted: tests/test_posthog_exception_noise.py
         fails the build the day a theme file gains one, which is the only thing
         that keeps a point-in-time grep true. The recorded profile comes from
         the PostHog stack trace read on 2026-07-27 (the "~13:35" entry in
         sacrasoul-brain/agents/aside/memory-archive-mirror/episodic/2026-07-27.md):
         function `sendPageHideMessage`, attributed to the DOCUMENT itself at
         line 1 — an inline script Meta's in-app browser pushes into the
         WKWebView on pagehide, present in no served HTML and in no file we
         fetch. Every occurrence carried an Instagram iPhone user agent.

         So it drops only where ALL THREE line up with that evidence: the
         message names the bridge, the stack names no file of its own — the
         document, an anonymous inline script — or carries no frame at all,
         AND the user agent says an in-app browser (verified live 2026-09-21:
         8 of 8 occurrences in 14 days carried Instagram's marker, none a
         plain Safari UA). The same message thrown from a real script FILE —
         a theme asset today, a vendor tag tomorrow — is KEPT, and so is the
         whole class in a regular Safari, where no in-app host exists to have
         injected it: if the finding's original title ever comes true, Error
         Tracking will show it.

         What this does NOT do is stop the throw. The throwing code is Meta's;
         it arrives at runtime from the app, not from anything we serve or
         fetch, and nothing in the theme can guard it — the reported "fix the
         unguarded reference" is not implementable here. This is telemetry
         hygiene, and the customer-side exception in an Instagram or Facebook
         in-app browser is unchanged by it. */
      before_send: function (event) {
        try {
          if (!event || event.event !== "$exception") return event;
          var first = (event.properties && event.properties.$exception_list || [])[0] || {};
          var msg = String(first.value || "");
          var noise = [
            /Java object is gone|Java exception was raised|_AutofillCallbackHandler/,
            /importScripts.*web-pixels/
          ];
          for (var i = 0; i < noise.length; i++) if (noise[i].test(msg)) return null;
          if (/webkit\.messageHandlers/.test(msg) && !thrownFromAScriptFile(first) && inAppBrowserUA()) return null;
        } catch (e) {}
        return event;
      },
      /* Heatmaps OFF. The overlay is the one capability here nobody has read
         in the record: the daily tripwire reports rage clicks, dead clicks,
         funnels and vitals, never a heatmap. It costs a pointer-position
         listener plus a periodic $$heatmap_data flush on every page. One word
         back to true if Sho wants the overlay again.
         Autocapture stays wide open, deliberately, against the brief's
         suggested allowlists. Reading the shipped array.js: $rageclick is
         emitted by a recursive call into the SAME filter as $autocapture, so an
         element_allowlist of a/button/form/input/select/textarea would silently
         drop rage clicks on everything else — and the tripwire's own friction
         section names targets like a price chip and a flag emoji. The allowlist
         would buy nothing in return: autocapture ships inside array.js, its
         listeners are installed either way, and the filter runs per click, not
         during load. dom_event_allowlist ['click','change','submit'] is exactly
         what already reaches the capture path, so it is a no-op. */
      capture_heatmaps: false,
      opt_out_useragent_filter: false,
      loaded: function (ph) {
        /* FIRST, before anything else in this callback. posthog-js calls
           loaded(ph) and only then captures the initial $pageview, and the
           web-vitals extension loads later still. A register() merely queued on
           the stub before init() is replayed AFTER init, so it cannot promise
           to beat that first pageview — this can. Without it the opening
           pageview and, worse, $web_vitals of an internal session go out
           untagged, which is the exact leak the flag exists to close. */
        /* No consent now, but posthog-js remembers an opt-in from an earlier
           visit (Shopify's consent expired or was cleared, PostHog's storage was
           not): under "on_reject" a stored opt-in wins over the cookieless
           default and would run full tracking. Opt out explicitly first. */
        try {
          if (consentState !== true && ph.get_explicit_consent_status && ph.get_explicit_consent_status() === "granted") {
            ph.opt_out_capturing();
          }
        } catch (e) {}
        registerContext(ph);
        /* A visitor may grant again after a prior PostHog opt-out persisted.
           Clear that flag silently so capture resumes. Replay is never started
           here — the attention gate below owns that single decision. */
        try {
          if (consentState === true && ph.has_opted_out_capturing && ph.has_opted_out_capturing()) {
            ph.opt_in_capturing({ captureEventName: false });
          }
        } catch (e) {}
        if (!autoPageview) {
          /* Cookieless start (see capture_pageview): a grant during the
             download was opted in just above, which reloads persistence, so
             register once more, then send the opening $pageview ourselves. */
          registerContext(ph);
          try { ph.capture("$pageview"); } catch (e) {}
        }
        /* Replay's signal trigger (see the comment above
           onEventCapturedForReplay): registered once, here, rather than in
           applyConsentState's grant branch, because loaded() only runs once
           per page life whether consent was granted on load or after a
           revoke/regrant — one registration point, no re-arming logic. */
        try { ph.on("eventCaptured", onEventCapturedForReplay); } catch (e) {}
        sdkReady = true;
        if (replayWanted) startReplayDiagnostics(ph);
      }
    });

    installBehaviorBridge();

    /* Tag before anything else is captured, so the very first pageview of an
       internal session carries the flag too. The preview bar arrives late, so
       look once more after the page settles. */
    function flagInternal() {
      var reason = internalReason();
      if (reason === "asked") {
        try { posthog.register({ ss_internal: true }); } catch (e) {}
        if (consentState === true) {
          try { posthog.setPersonProperties({ ss_internal: true }); } catch (e) {}
        }
      } else if (reason === "driven") {
        try { posthog.register_for_session({ ss_internal: true }); } catch (e) {}
      }
    }
    var reasonAtInit = internalReason();
    if (reasonAtInit !== null) flagInternal();
    /* The late look must run whenever the strongest reason so far is weaker
       than "asked", not just when there was no reason at all: a DRIVEN browser
       whose preview bar arrives late is still owed its durable flag, and
       flagInternal() re-derives the reason so the upgrade takes the durable
       doors. */
    if (reasonAtInit !== "asked") {
      setTimeout(function () { if (internalReason() === "asked") flagInternal(); }, 3000);
    }

    if (/\/products\//.test(path)) {
      ssCapture("ss_product_viewed", {
        handle: path.split("/products/")[1],
        locale: document.documentElement.lang,
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || null,
        market: (window.Shopify && Shopify.country) || null,
        page: path
      });
    }

    // ---- P4.1 segmented experience RUM ----
    // Load deferred, ONLY here — this runs once PostHog is live: in cohort, and
    // (since 2026-09-27) for consented and cookieless visitors alike.
    // The URL comes from data-rum-src on our own tag: this file is a static
    // asset, so it cannot render Liquid's asset_url itself. No fallback path —
    // a guessed URL would 404 quietly and look like RUM was simply silent.
    try {
      var rumTag = document.getElementById("ss-posthog");
      var rumSrc = rumTag ? rumTag.getAttribute("data-rum-src") : "";
      if (rumSrc) {
        var rum = document.createElement("script");
        rum.src = rumSrc;
        rum.defer = true;
        document.head.appendChild(rum);
      }
    } catch (e) {}
  }

  // ---- 3. Consent + attention gate ----
  // Google Consent Mode remains the authority. An explicit dataLayer update is
  // also read directly because Stape can receive the command before
  // google_tag_data materialises. Regional defaults are never guessed.
  var explicitAnalyticsConsent = null;
  var consentScanIndex = 0;

  function readConsentCommand(item) {
    try {
      if (!item || item[0] !== "consent" || item[1] !== "update") return;
      var value = item[2] && item[2].analytics_storage;
      if (value === "granted") explicitAnalyticsConsent = true;
      if (value === "denied") explicitAnalyticsConsent = false;
    } catch (e) {}
  }

  function scanConsentUpdates() {
    try {
      var dl = window.dataLayer || [];
      if (dl.length < consentScanIndex) consentScanIndex = 0;
      while (consentScanIndex < dl.length) readConsentCommand(dl[consentScanIndex++]);
    } catch (e) {}
  }

  /* google_tag_data is built by gtag.js, which this store loads from its own
     tracking subdomain. A visitor whose DNS, VPN or extension blocks that host
     never gets the object, and PostHog would then sit dark for the whole visit
     even where the store's own declared policy plainly allows it. Those
     declarations are already in dataLayer, first-party, before any Google tag
     runs — so resolve them exactly as Google would: the last default whose
     region covers this visitor wins. EEA/UK/CH still resolves to denied and
     waits for the banner. No country, no answer: silence is never a grant. */
  function readDeclaredDefault() {
    var country = null;
    try { country = (window.Shopify && window.Shopify.country) || null; } catch (e) {}
    if (!country) return null;
    country = String(country).toUpperCase();
    var resolved = null;
    try {
      var dl = window.dataLayer || [];
      for (var i = 0; i < dl.length; i++) {
        var item = dl[i];
        if (!item || item[0] !== "consent" || item[1] !== "default") continue;
        var cfg = item[2];
        if (!cfg || cfg.analytics_storage === undefined) continue;
        if (cfg.region && cfg.region.indexOf(country) === -1) continue;
        resolved = cfg.analytics_storage === "granted";
      }
    } catch (e) {}
    return resolved;
  }

  function analyticsGranted() {
    scanConsentUpdates();
    /* A consent update is the visitor's explicit choice and outranks any stale
       regional default that google_tag_data has not reconciled yet. */
    if (explicitAnalyticsConsent !== null) return explicitAnalyticsConsent;
    /* Shopify's customer privacy API is the banner's own record, upstream of
       the Google bridge. A grant there counts at once (a returning shopper who
       accepted earlier); anything else falls through to Google consent mode. */
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === "function" && cp.analyticsProcessingAllowed() === true) return true;
    } catch (e) {}
    try {
      var e = window.google_tag_data && window.google_tag_data.ics
        && window.google_tag_data.ics.entries && window.google_tag_data.ics.entries.analytics_storage;
      if (e) {
        var eff = (e.update !== undefined) ? e.update
                : (e.default !== undefined) ? e.default
                : e.declare;
        if (eff === true || eff === false) return eff;
      }
    } catch (e) {}
    return readDeclaredDefault();
  }

  /* What the VISITOR answered the cookie banner, read from Shopify's own record,
     apart from what the store sends. Since 2026-10-06 the two differ: Consent
     Mode defaults to granted in every region and the banner pushes no update
     (decision sacrasoul-brain/strategy/decisions/2026-10-06-klaviyo-and-google-
     consent-default-granted.md), so analyticsGranted() above is true for
     everyone and capture runs in full mode for everyone. The answer itself still
     decides two things here: session replay (never for a visitor who declined
     or has not answered; see replayAllowed()) and the ss_consent label.
       granted   analytics allowed by Shopify: they accepted, or no banner is
                 required where they are (Oman, the US, ...)
       declined  they answered and analytics is not allowed
       pending   a banner nobody has answered yet
       null      no Shopify API (blocked, or not loaded yet): unknown, and then
                 no banner can have been answered either */
  var visitorAnswer = null; // set by visitorConsentCollected on this page
  function visitorChoice() {
    if (visitorAnswer) return visitorAnswer;
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || typeof cp.analyticsProcessingAllowed !== "function") return null;
      if (cp.analyticsProcessingAllowed() === true) return "granted";
      var c = typeof cp.currentVisitorConsent === "function" ? cp.currentVisitorConsent() : null;
      if (c && c.analytics === "no") return "declined";
      if (c && c.analytics === "") return "pending";
      if (typeof cp.shouldShowBanner === "function" && cp.shouldShowBanner() === false) return "declined";
      return "pending";
    } catch (e) { return null; }
  }

  /* Replay is the one thing that still follows the visitor's own answer: exactly
     the visitors who were recorded before 2026-10-06 (accepted, or no banner
     required) are recorded now. Declined and unanswered never start rrweb. */
  function replayAllowed() {
    if (consentState !== true) return false;
    var choice = visitorChoice();
    return choice === "granted"; // only an explicit yes; an unknown answer (API blocked or not loaded yet) never records
  }

  /* The banner's answer, when it lands mid-page. A decline stops a running
     recording; capture carries on (it no longer follows the banner). An accept
     lets the next signal event start replay. */
  document.addEventListener("visitorConsentCollected", function (event) {
    var detail = (event && event.detail) || {};
    var analytics = detail.analyticsAllowed !== undefined ? detail.analyticsAllowed : detail.analytics;
    if (analytics === true) visitorAnswer = "granted";
    else if (analytics === false) visitorAnswer = "declined";
    else visitorAnswer = null;
    if (!replayAllowed()) {
      replayWanted = false;
      if (window.__ssPhReplayStarted) {
        window.__ssPhReplayStarted = false;
        try { if (window.__ssPhInit) posthog.stopSessionRecording(); } catch (e) {}
      }
    }
    noteConsentLabel(false);
  });

  /* What the visitor has told us, as one word: visitorChoice() when Shopify
     answers. Without the Shopify API it falls back to the Consent Mode reads
     above, which can only say "granted" now that the store default is granted
     everywhere (they still distinguish the three states in a page whose
     dataLayer carries a denied default or update, which the harness uses).
     Before this, a cookieless event could not say whether its visitor refused
     or simply never answered. */
  function consentLabel() {
    var choice = visitorChoice();
    if (choice) return choice;
    if (analyticsGranted() === true) return "granted";
    if (explicitAnalyticsConsent === false) return "declined";
    try {
      var e = window.google_tag_data && window.google_tag_data.ics
        && window.google_tag_data.ics.entries && window.google_tag_data.ics.entries.analytics_storage;
      if (e && e.update === false) return "declined";
    } catch (e) {}
    return "pending";
  }

  /* Re-register ss_consent when the answer changes after the SDK started.
     `force` after an opt-in/opt-out, which reloads PostHog's persistence. */
  function noteConsentLabel(force) {
    var label = consentLabel();
    if (label === lastConsentLabel && !force) return;
    lastConsentLabel = label;
    if (!window.__ssPhInit) return;  // registerContext() registers it at start
    try { posthog.register({ ss_consent: label }); } catch (e) {}
  }

  /* The cart courier (assets/ss-attribution.js, the __ss_ph cart attribute that
     joins an order to its PostHog person) asks at send time whether this
     browser's PostHog id may go on the cart. Until 2026-10-06 that was Shopify's
     analyticsProcessingAllowed(), so a declined or unanswered EEA shopper's
     order carried no id. It now follows the same answer capture does
     (analyticsGranted(): the store's Consent Mode, granted everywhere since
     2026-10-06), so every order can be joined. ss-attribution.js still sends
     the id only when it is a real persistent one (distinct id === device id),
     never a cookieless hash. */
  window.SSPosthogAnalyticsAllowed = function () {
    return analyticsGranted() === true;
  };



  /* ---- Boot timing (2026-09-11) ----
     Until now the SDK initialised the moment consent resolved. Outside the EEA
     that is during parse; inside it, the instant the banner is answered. Either
     way array.js (94 KiB, ~405-425 ms of main thread in the PageSpeed trace at
     4x) lands while the customer is still waiting for the hero to paint, and
     Lighthouse — which never touches the page — pays for all of it.

     Now the SDK waits for whichever comes first:
       - the first real interaction (pointerdown, keydown, touchstart, scroll), or
       - a genuinely idle frame (requestIdleCallback with NO timeout, so it can
         only run on a frame the browser had spare).
     and the init itself then rides a FRESH idle callback, so the 400 ms of
     parse+init can never land inside the tap that woke it. That inner callback
     does carry a 2 s timeout: it starts only after the wake, so it is never in
     the load window, and it guarantees a shopper who taps add-to-cart on a busy
     phone is initialised in time to send it.

     Bouncers still get $pageview. A visitor who reads the fold and leaves never
     interacts, so the idle path is the one that fires: `capture_pageview: true`
     means the initial $pageview goes out as soon as init runs, and on a page
     that has finished its work rIC fires within the first idle period. What is
     genuinely given up: a visitor who leaves BEFORE the browser is ever idle,
     and a load in a background tab (rIC is deferred while hidden). Both are
     sessions where no paint the customer saw was ever measured anyway.

     Safari has never shipped requestIdleCallback, so iOS takes the fallback —
     and iOS is not what Lighthouse runs. A flat 8 s timer there would silently
     drop the $pageview of every iPhone visitor who leaves inside 8 s, which is
     most of them. So the fallback is `load` + 1.5 s instead: after the load
     event by construction, therefore out of the critical path, and still early
     enough that the session is counted. */
  var initArmed = false;
  /* "wheel", not "scroll": browsers fire a trusted scroll by themselves during
     load, which pulled array.js into the first-paint window (the GTM loader
     made the same change on 2026-09-11). Touch scrolls wake it via touchstart. */
  var initArmEvents = ["pointerdown", "keydown", "touchstart", "wheel"];
  var pageLoaded = document.readyState === "complete";
  window.addEventListener("load", function () { pageLoaded = true; }, { once: true });
  var initIdleHandle = null;
  var initTimer = null;
  var INIT_NO_IDLE_DELAY_MS = 1500;
  var PRIVACY_WAIT_MS = 4000;
  /* Harnesses set window.__ssIdleAfterLoadMs = 0; production never defines it. */
  var IDLE_AFTER_LOAD_MS = typeof window.__ssIdleAfterLoadMs === "number" ? window.__ssIdleAfterLoadMs : 5000;

  function disarmInit() {
    initArmEvents.forEach(function (name) {
      window.removeEventListener(name, wakeInit, { capture: true });
    });
    window.removeEventListener("load", armInitFallbackTimer);
    if (initIdleHandle && window.cancelIdleCallback) cancelIdleCallback(initIdleHandle);
    if (initTimer) clearTimeout(initTimer);
    initIdleHandle = null; initTimer = null;
  }

  function wakeInit() {
    if (!initArmed) return;
    disarmInit();
    var run = function () {
      if (consentState === true) { initPosthog(); return; }
      /* Not granted yet. A returning EEA shopper who already accepted reads as
         denied until the Shopify -> Google bridge pushes the grant (~2.5 s, see
         snippets/google-consent-mode.liquid), so wait for Shopify's own answer
         (the shared resolver), at most PRIVACY_WAIT_MS, then decide: their
         opening $pageview must not go out cookieless. */
      var started = false;
      var go = function () {
        if (started) return;
        started = true;
        applyConsentState();
        initPosthog(); // full if granted by now, cookieless otherwise
      };
      try {
        if (window.SacrasoulPrivacyReady && window.SacrasoulPrivacyReady.ready) {
          window.SacrasoulPrivacyReady.ready.then(go, go);
          setTimeout(go, PRIVACY_WAIT_MS);
          return;
        }
      } catch (e) {}
      go();
    };
    if (window.requestIdleCallback) requestIdleCallback(run, { timeout: 2000 });
    else setTimeout(run, 0);
  }

  function armInitFallbackTimer() {
    if (!initArmed || initTimer) return;
    initTimer = setTimeout(wakeInit, INIT_NO_IDLE_DELAY_MS);
  }

  function scheduleInit() {
    if (window.__ssPhInit || initArmed) return;
    initArmed = true;
    initArmEvents.forEach(function (name) {
      window.addEventListener(name, wakeInit, { passive: true, capture: true });
    });
    /* The idle path waits for `load` first. requestIdleCallback also fires
       during the network wait before load (measured 2026-09-11: array.js at
       2.0 s with load at 3.5 s on a fresh profile), which is exactly the window
       this gate exists to protect. After load, the first genuinely idle frame. */
    var armIdle = function () {
      if (!initArmed || initIdleHandle || initTimer) return;
      /* Not the first idle frame after load: the first one lands a few ms after
         it, inside the window Lighthouse still traces and a slow phone is still
         painting the fold (measured 2026-09-11: array.js 371 ms of main thread
         in a PSI run, starting 18 ms after load). Five quiet seconds first;
         the interaction path above still wakes it at once. */
      initTimer = setTimeout(function () {
        initTimer = null;
        if (!initArmed) return;
        if (window.requestIdleCallback) initIdleHandle = requestIdleCallback(wakeInit);
        else armInitFallbackTimer();
      }, IDLE_AFTER_LOAD_MS);
    };
    if (pageLoaded) armIdle();
    else window.addEventListener("load", armIdle, { once: true });
  }

  /* Replay is armed by a SIGNAL, not by presence (Sho, 2026-09-12): every
     session used to record once a visitor did anything and then went quiet.
     That is a bounce filter, not the "only when something goes wrong, double
     clicks or rage clicks" Sho asked for. Now the trigger is one of four
     captured events — $rageclick (posthog-js's own autocapture, already on),
     $dead_click (capture_dead_clicks, on since 2026-09-27), $exception (capture_exceptions above, already on), or ss_cart_error (the
     cart drawer's own reportCartError, assets/sacrasoul-cart-drawer.js) — read
     via the public posthog.on('eventCaptured', ...) hook registered once the
     SDK is loaded(), below. The init flag that leaves recording off means rrweb never
     starts for ANY session until this fires — confirmed against the shipped
     posthog-js source (lazy-loaded-session-recorder.ts): a project-side
     sampling or event/URL trigger still starts the recorder unconditionally
     and buffers rrweb snapshots in memory until its trigger matches, so it
     pays the same main-thread cost (recorder init + mutation observers) on
     every session regardless of the ingestion outcome. This client-side gate
     is the one place that actually removes that cost — the trade is real,
     not free: a trigger-started recording has no pre-trigger buffer, so it
     shows what happened AFTER the rage click, not the hesitation before it.
     A trigger is already evidence something is wrong, so it starts on
     the next idle frame, with a 1000ms timeout so a busy main thread cannot
     stall it past the moment that matters. */
  function isReplayTriggerEvent(name) {
    return name === "$rageclick" || name === "$dead_click" || name === "$exception" || name === "ss_cart_error";
  }
  function onEventCapturedForReplay(captured) {
    if (!captured || !isReplayTriggerEvent(captured.event)) return;
    if (window.__ssPhReplayStarted || !replayAllowed()) return;
    scheduleSignalReplayStart();
  }

  /* A trigger is already evidence something is wrong: start on the next idle
     frame, capped at a 1000ms timeout. */
  function scheduleSignalReplayStart() {
    if (window.__ssPhReplayStarted || !replayAllowed()) return;
    var beginNow = function () {
      if (!replayAllowed() || window.__ssPhReplayStarted) return;
      startReplayNow();
    };
    if (window.requestIdleCallback) requestIdleCallback(beginNow, { timeout: 1000 });
    else setTimeout(beginNow, 0);
  }

  function startReplayNow() {
    if (window.__ssPhReplayStarted || !replayAllowed()) return;
    window.__ssPhReplayStarted = true;
    replayWanted = true;
    if (sdkReady && beginReplay) beginReplay();
  }

  function applyConsentState() {
    var granted = analyticsGranted();
    /* pending -> declined does not move `granted` (both false), so the label
       is checked before the early returns, not only on a change. */
    noteConsentLabel(false);
    if (granted === null) return false;
    if (granted === consentState) return true;
    consentState = granted;
    window.__ssPosthogConsent = granted;
    if (granted) {
      /* Since 2026-10-05 the opt-in is made durable when it is typed, at any
         consent state. This copy remains for a tab whose session flag predates
         that release. */
      persistInternalOptIn();
      /* Capture starts the moment consent lands, so a confirmed add or checkout
         is never stranded in the queue by a visitor who leaves quickly. */
      if (window.__ssPhInit) {
        try { posthog.opt_in_capturing({ captureEventName: false }); } catch (e) {}
        try { if (registerContextNow) registerContextNow(posthog); } catch (e) {}
      } else {
        scheduleInit();
      }
      /* Replay's trigger arm is not wired here: the
         posthog.on('eventCaptured', ...) listener registered in loaded()
         below persists across a revoke/regrant within the same page life. */
    } else {
      /* A decline no longer stops capture: it turns it cookieless (explicit
         opt-out under cookieless_mode "on_reject"). Replay stops for good. */
      replayWanted = false;
      window.__ssPhReplayStarted = false;
      if (window.__ssPhInit) {
        try { posthog.stopSessionRecording(); } catch (e) {}
        try { posthog.opt_out_capturing(); } catch (e) {}
      }
      /* get_distinct_id() remains readable after opt-out. Tell the attribution
         courier that analytics consent is gone and re-state the complete
         __ss_* namespace; its __ss_ph value becomes "", Shopify's delete verb.
         A failed write is not fingerprinted, so the next safe cart event
         retries it. */
      try {
        if (window.SSAttribution && window.SSAttribution.sync) {
          window.SSAttribution.sync();
        }
      } catch (e) {}
    }
    noteConsentLabel(true);
    return true;
  }

  function waitForConsent() {
    scheduleInit(); // every visitor is counted; consent decides cookieless vs full
    applyConsentState();
    try {
      var dl = window.dataLayer = window.dataLayer || [];
      var origPush = dl.push;
      dl.push = function () {
        for (var i = 0; i < arguments.length; i++) readConsentCommand(arguments[i]);
        var result = origPush.apply(dl, arguments);
        try { applyConsentState(); } catch (e) {}
        return result;
      };
    } catch (e) {}
    var tries = 0;
    var iv = setInterval(function () {
      if (consentState === true || ++tries > 120) clearInterval(iv);
      else applyConsentState();
    }, 500);
  }

  /* Prerender gate (2026-09-11): product links are prerendered
     (snippets/speculation-rules.liquid). Nothing here may run for a page the
     shopper never activates, so a prerendered document waits for
     prerenderingchange; every other document starts exactly as before. */
  function startWhenVisible(fn) {
    if (document.prerendering) document.addEventListener("prerenderingchange", fn, { once: true });
    else fn();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { startWhenVisible(waitForConsent); }, { once: true });
  } else { startWhenVisible(waitForConsent); }
})();
